import Card from "components/molecules/Card";
import useQueryBuildings from "ApiLayer/Building/queryBuildings";

interface BuildingCumulativeOperatingCostCardProps {
    buildingModel: Building;
    selectedBMCRP: BuildingModelCarbonReductionPlan;
    isBaseline: boolean;
    year: number | null;
}

export function BuildingCumulativeOperatingCostCard({
    buildingModel,
    selectedBMCRP,
    isBaseline,
    year,
}: BuildingCumulativeOperatingCostCardProps) {
    const runningTotals = {
        decarb: {
            landlordSplitBasis: "both",
            utilityCost: 0,
            carbonPolicyCost: 0,
        },
        baseline: {
            landlordSplitBasis: "both",
            utilityCost: 0,
            carbonPolicyCost: 0,
        },
        totalGFA: 0,
    };
    runningTotals.totalGFA += buildingModel.grossFloorArea;

    const crp = selectedBMCRP.plan;
    runningTotals.decarb.landlordSplitBasis = crp.landlordSplitBasis;
    crp.annualSummaries.forEach((aSum) => {
        if (!year || aSum.year === year) {
            runningTotals.decarb.utilityCost += aSum.splitAnnualUtilityCost;
            runningTotals.decarb.carbonPolicyCost += aSum.carbonPolicyCosts;
        }
    });
    const baselineCrp = selectedBMCRP.baselinePlan;
    runningTotals.baseline.landlordSplitBasis = baselineCrp.landlordSplitBasis;
    baselineCrp.annualSummaries.forEach((aSum) => {
        if (!year || aSum.year === year) {
            runningTotals.baseline.utilityCost += aSum.splitAnnualUtilityCost;
            runningTotals.baseline.carbonPolicyCost += aSum.carbonPolicyCosts;
        }
    });

    const thePlan = isBaseline ? runningTotals.baseline : runningTotals.decarb;
    const formattedData: CumulativeOperatingCostCardData = {
        landlordSplitBasis: thePlan.landlordSplitBasis,
        totalUtilityCost: thePlan.utilityCost,
        totalUtilityCostPercentChange:
            ((runningTotals.baseline.utilityCost - runningTotals.decarb.utilityCost) /
                runningTotals.baseline.utilityCost) *
            100,
        totalUtilityCostIntensity: thePlan.utilityCost / runningTotals.totalGFA,
        totalCarbonPolicyCost: thePlan.carbonPolicyCost,
        totalCarbonPolicyCostPercentChange:
            ((runningTotals.decarb.carbonPolicyCost - runningTotals.baseline.carbonPolicyCost) /
                runningTotals.baseline.carbonPolicyCost) *
            100,
        totalCarbonPolicyCostIntensity: thePlan.carbonPolicyCost / runningTotals.totalGFA,
    };
    return <CumulativeOperatingCostCard data={formattedData} isLoading={!selectedBMCRP} isBaseline={isBaseline} />;
}

interface ReportsCumulativeOperatingCostCardProps {
    filtersetUid: string;
    reportedVsPublished: string;
    isBaseline: boolean;
    year: number | null;
}

export function ReportsCumulativeOperatingCostCard({
    filtersetUid,
    reportedVsPublished,
    isBaseline,
    year,
}: ReportsCumulativeOperatingCostCardProps) {
    const { isLoading, data: buildingsResponse } = useQueryBuildings(filtersetUid, reportedVsPublished);

    if (isLoading) {
        return <Card data={undefined} loading={true} cardTitle="Operating Cost" />;
    }

    const runningTotals = {
        decarb: {
            utilityCost: 0,
            carbonPolicyCost: 0,
        },
        baseline: {
            utilityCost: 0,
            carbonPolicyCost: 0,
        },
        totalGFA: 0,
    };

    let landlordSplitBasis: string = "unk";
    buildingsResponse!.buildings.forEach((building) => {
        if (!building.reportedCrp || !building.baselineCrp) {
            return;
        }
        if (landlordSplitBasis === "unk") {
            landlordSplitBasis = building.reportedCrp.landlordSplitBasis;
        } else if (landlordSplitBasis !== building.reportedCrp.landlordSplitBasis) {
            landlordSplitBasis = "mixed";
        }
        runningTotals.totalGFA += building.grossFloorArea;

        const crp: CarbonReductionPlan = building.reportedCrp!;
        crp.annualSummaries.forEach((aSum) => {
            if (!year || aSum.year === year) {
                runningTotals.decarb.utilityCost += aSum.splitAnnualUtilityCost;
                runningTotals.decarb.carbonPolicyCost += aSum.carbonPolicyCosts;
            }
        });
        const baselineCrp: CarbonReductionPlan = building.baselineCrp!;
        baselineCrp.annualSummaries.forEach((aSum) => {
            if (!year || aSum.year === year) {
                runningTotals.baseline.utilityCost += aSum.splitAnnualUtilityCost;
                runningTotals.baseline.carbonPolicyCost += aSum.carbonPolicyCosts;
            }
        });
    });

    const formattedData: CumulativeOperatingCostCardData = {
        landlordSplitBasis,
        totalUtilityCost: runningTotals.decarb.utilityCost,
        totalUtilityCostPercentChange:
            ((runningTotals.baseline.utilityCost - runningTotals.decarb.utilityCost) /
                runningTotals.baseline.utilityCost) *
            100,
        totalUtilityCostIntensity: runningTotals.decarb.utilityCost / runningTotals.totalGFA,
        totalCarbonPolicyCost: runningTotals.decarb.carbonPolicyCost,
        totalCarbonPolicyCostPercentChange:
            ((runningTotals.decarb.carbonPolicyCost - runningTotals.baseline.carbonPolicyCost) /
                runningTotals.baseline.carbonPolicyCost) *
            100,
        totalCarbonPolicyCostIntensity: runningTotals.decarb.carbonPolicyCost / runningTotals.totalGFA,
    };
    return <CumulativeOperatingCostCard data={formattedData} isLoading={isLoading} isBaseline={isBaseline} />;
}

interface CumulativeOperatingCostCardProps {
    data: CumulativeOperatingCostCardData | undefined;
    isBaseline: boolean;
    isLoading: boolean;
}

function CumulativeOperatingCostCard({ data, isLoading, isBaseline }: CumulativeOperatingCostCardProps) {
    if (!data) {
        return <Card loading={isLoading} data={undefined} cardTitle="Cumulative Operating Cost" />;
    }
    const asPercentage = (isBaseline: boolean, aValue: number) => {
        if (isBaseline) {
            return undefined;
        }
        if (Number.isNaN(aValue)) {
            return 0;
        }
        return aValue;
    };

    const sections = [
        [
            {
                displayInfoKey: "landlordSplitBasis",
                value: data.landlordSplitBasis,
            },
            {
                displayInfoKey: "utilityCost",
                value: data.totalUtilityCost,
                tooltipText: "Excluding carbon policy cost",
                increaseDecreasePercentage: asPercentage(isBaseline, -data.totalUtilityCostPercentChange),
            },
            {
                displayInfoKey: "utilityCostIntensity",
                value: data.totalUtilityCostIntensity,
                increaseDecreasePercentage: asPercentage(isBaseline, -data.totalUtilityCostPercentChange),
            },
            {
                displayInfoKey: "carbonPolicyCost",
                value: data.totalCarbonPolicyCost,
                increaseDecreasePercentage: asPercentage(isBaseline, data.totalCarbonPolicyCostPercentChange),
            },
            {
                displayInfoKey: "carbonPolicyCostIntensity",
                value: data.totalCarbonPolicyCostIntensity,
                increaseDecreasePercentage: asPercentage(isBaseline, data.totalCarbonPolicyCostPercentChange),
            },
        ],
    ];

    const formattedData = {
        header: {
            displayInfoKey: "operatingCost",
            value: data.totalUtilityCost + data.totalCarbonPolicyCost,
            valueColor: isBaseline ? undefined : "var(--audette-content-positive)",
        },
        sections,
    };

    return <Card loading={isLoading} data={formattedData} cardTitle="Cumulative Operating Cost" />;
}
